import D from "@/admin/dashboard/Dashboard.module.css";

import { AdminLocationMenu } from "@/admin/AdminLocationSelect.tsx";
import { SmallDonut } from "@/admin/dashboard/DonutChart.tsx";
import { LightBlock } from "@/admin/dashboard/LightBlock.tsx";
import { abbreviateNumber } from "@/admin/dashboard/utils.ts";
import { WBarChart, type WBarChartOptions } from "@/admin/dashboard/WBarChart.tsx";
import { servicesStatisticsQuery } from "@/admin/services/servicesQuery.ts";
import { useTooltipPortal } from "@/admin/utils/useTooltipPortal.tsx";
import { BadgeDot } from "@/components/Badge.tsx";
import { Separator } from "@/components/Separator.tsx";
import { useStandardSuspenseQuery } from "@/modules/api/useStandardMutation.ts";
import { usePricingLocations } from "@/modules/location/locationQuery.ts";
import { useThemeVar } from "@/modules/theme/useTheme.ts";
import type { BarSeriesOption } from "echarts/charts";
import { useState } from "react";
import { entries, groupBy, unique } from "remeda";
import invariant from "tiny-invariant";

function ManagedServicesSectionContent() {
    const status = ["active", "deleted"];
    const services = useStandardSuspenseQuery(servicesStatisticsQuery, { status });
    const locations = usePricingLocations();
    const [checkedLocations, setCheckedLocations] = useState<string[]>(() => locations.map((item) => item.slug));

    const colorWhite = useThemeVar("color-white");
    const colorTextHoverAlpha = useThemeVar("color-text-hover-alpha");
    const colorText = useThemeVar("color-text");

    const colorCharts = [
        undefined,
        useThemeVar("color-chart-1"),
        useThemeVar("color-chart-2"),
        useThemeVar("color-chart-3"),
    ];

    const serviceTypes = unique(services.data.map((item) => item.service));

    const locationServices = services.data.filter((item) => {
        const location = item.properties?.location;
        return typeof location === "string" && checkedLocations.includes(location);
    });

    const total = locationServices.filter((item) => !item.is_deleted);
    const groupedTotal = groupBy(total, (l) => l.service);

    const serviceItems = entries(groupedTotal).map(([_type, value], index) => {
        return {
            value: (value.length / total.length) * 100,
            color: `var(--color-chart-${index + 1})`,
        };
    });

    const tooltip = (
        <div>
            {entries(groupedTotal).map(([type, value], index) => (
                <BadgeDot key={type} color={`color-chart-${index + 1}`}>
                    {type}: <b>{value.length}</b>
                </BadgeDot>
            ))}
        </div>
    );

    const groupedItems = groupBy([...locationServices], (l) => l.service);

    const { portal, formatter } = useTooltipPortal(({ value }) => {
        invariant(typeof value === "number");
        return (
            <div>
                {entries(groupedItems).map(([type, value], index) => {
                    const deleted = value.filter((item) => item.is_deleted);
                    return (
                        <BadgeDot key={index} color={`color-chart-${index + 1}`}>
                            {type}: <b>{value.length}</b> (deleted: {deleted.length})
                        </BadgeDot>
                    );
                })}
            </div>
        );
    });

    const seriesData = entries(groupedItems).map(([type, value], index): BarSeriesOption => {
        return {
            name: type,
            type: "bar",
            stack: "total",

            itemStyle: {
                color: colorCharts[index + 1],
            },
            tooltip: { formatter },
            data: [value.length],
        };
    });

    const options: WBarChartOptions = {
        tooltip: {
            trigger: "item",
            axisPointer: {
                type: "none",
            },
            borderWidth: 0,
            backgroundColor: colorWhite,
            textStyle: {
                color: colorText,
            },
            extraCssText: "box-shadow: 0 8px 20px rgba(0 0 0 / 10%);",
        },
        grid: {
            top: "5px",
            left: "5px",
            right: "5px",
            bottom: "5px",
            containLabel: true,
        },
        xAxis: {
            show: false,
            type: "category",
        },
        yAxis: {
            type: "value",
            axisLabel: {
                formatter: (value) => abbreviateNumber(value),
            },
            splitLine: {
                lineStyle: {
                    color: colorTextHoverAlpha,
                },
            },
        },
        series: seriesData,
    };

    function onChangeLocation(value: string[]) {
        setCheckedLocations(value);
    }

    return (
        <>
            {portal}

            <div className={D.ItemHeader}>
                <div>
                    <h2 className="font-size-heading">Managed Services</h2>
                    <p>Distribution by service type</p>
                </div>

                <SmallDonut
                    marginLeft
                    title="Total number of Managed Services"
                    items={serviceItems}
                    tooltip={tooltip}
                    total={total.length}
                />
            </div>

            <Separator />

            <div className="flex justify-between pt-3 pb-4">
                <div className="flex gap-1rem font-size-small">
                    {serviceTypes.map((type, index) => (
                        <BadgeDot key={index} color={`color-chart-${index + 1}`}>
                            {type}
                        </BadgeDot>
                    ))}
                </div>

                <AdminLocationMenu
                    value={checkedLocations}
                    locations={locations}
                    deletedLocations={[]}
                    onChange={onChangeLocation}
                />
            </div>

            <WBarChart options={options} />
        </>
    );
}

export function ManagedServicesSection() {
    return (
        <LightBlock loadingHeight="420px">
            <ManagedServicesSectionContent />
        </LightBlock>
    );
}
